<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import InputText from '#components/elements/input/inputText/InputText.vue';
import PopUp from '#components/popUp/PopUp.vue';
import type { TreeItemsStateType } from '#components/treeView';
import {
  FILTERS_STATE_DEFAULT_VALUES,
  countFilters,
  type FilterNameType,
  type FiltersTreeType
} from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import {
  type FiltersStateType,
  type PerimeterFilterViewType,
  type ProductFilterConfigType,
  type ProductTaxonomyType
} from '@carrefour-gcs/shared';
import { refDebounced } from '@vueuse/core';
import { computed, ref, toRef, watch } from 'vue';
import CustomerSegmentationFilterDisclosure from './CustomerSegmentationFilterDisclosure.vue';
import FilterDisclosure from './FiltersDisclosure.vue';
import { usePerimeterFiltersData } from './composables/';

const props = defineProps<{
  show: boolean;
  taxonomy: ProductTaxonomyType;
  selectedFiltersTree: FiltersTreeType;
  productFilterConfig: ProductFilterConfigType;
  perimeterFilterView?: PerimeterFilterViewType;
}>();

const emit = defineEmits<{
  onSelectFilters: [
    state: {
      filtersState: FiltersTreeType;
    }
  ];
  onClose: [];
}>();

const { t } = useLanguage();

const checkedItemsState = ref<FiltersTreeType>({ ...props.selectedFiltersTree });
const globalSearch = ref<string>('');
const debouncedSearch = refDebounced(globalSearch, 600);

// Ensure reactivity from props to composables
const taxonomy = toRef(() => props.taxonomy);

const filterNeedingSearch: FilterNameType[] = [
  'barcodeFilters',
  'itemFilters',
  'competitorBaseItemFilters',
  'competitorBaseBarcodeFilters',
  'bemHoldingFilters',
  'bemMerchandiseFilters',
  'suppliersFilters',
  'brandsFilters'
];

const isEnabledPredicate = (filterName: FilterNameType) => {
  if (filterNeedingSearch.includes(filterName)) {
    const checkedFilters = Object.fromEntries(
      Object.entries(checkedItemsState.value[filterName]).filter(([, state]) => state.state === 'checked')
    );
    return !!Object.keys(checkedFilters).length || debouncedSearch.value.length > 1;
  }

  return true;
};

const filters = usePerimeterFiltersData({
  checkedItemsState,
  debouncedSearch,
  productFilterConfig: props.productFilterConfig,
  perimeterFilterView: props.perimeterFilterView,
  taxonomy,
  isEnabledPredicate
});

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      checkedItemsState.value = { ...props.selectedFiltersTree };
    }
  }
);

const isLoading = computed(() => {
  return Object.values(filters.value).some(({ isLoading, isEnabled }) => isLoading.value && isEnabled.value);
});

const nbFiltersSelected = computed(() => {
  const selectedFilters: FiltersStateType = { ...FILTERS_STATE_DEFAULT_VALUES };

  Object.keys(checkedItemsState.value).forEach((key) => {
    selectedFilters[key as FilterNameType] = Object.values(checkedItemsState.value[key as FilterNameType])
      .filter(({ state }) => state === 'checked')
      .map(({ nodeId }) => nodeId);
  });

  return countFilters(selectedFilters);
});

function handleOnCheckedItemsChange(checkedItems: TreeItemsStateType, key: FilterNameType) {
  checkedItemsState.value[key] = checkedItems;
}

function handleSelectFilters() {
  globalSearch.value = '';

  emit('onSelectFilters', {
    filtersState: checkedItemsState.value
  });
}

function handleClose() {
  globalSearch.value = '';
  checkedItemsState.value = { ...props.selectedFiltersTree };
  emit('onClose');
}

const isOnboarding = computed(() => {
  return (
    props.perimeterFilterView === 'onboardingNomenclature' || props.perimeterFilterView === 'onboardingStoresRegion'
  );
});

const searchInputLabel = computed(() =>
  t('filterPopin.searchBar', { productType: t(props.productFilterConfig.labelKey) })
);
</script>
<template>
  <PopUp :title="t('filterPopin.title')" :open="show" @on-close="handleClose">
    <div class="h-popupContent grid grid-rows-modal gap-y-4" :data-testid="`filters-popup`">
      <div>
        <InputText
          v-model="globalSearch"
          type="search"
          name="globalSearch"
          :label="searchInputLabel"
          :placeholder="searchInputLabel"
          icon="search"
        />
      </div>
      <div class="overflow-auto custom-vertical-scrollbar">
        <div
          v-if="
            !isLoading && Object.values(filters).every(({ items }) => !items.value || !items.value.children?.length)
          "
        >
          {{ t('commonLabel.noData') }}
        </div>
        <div v-else>
          <div v-for="[name, filter] in Object.entries(filters)" :key="name" class="overflow-auto max-h-full">
            <CustomerSegmentationFilterDisclosure
              v-if="
                name === 'consostyleMicroFilters' &&
                (filter.isAvailable || filters['lifestageMicroFilters']?.isAvailable)
              "
              :key="'customerSegmentation'"
              :consostyle-items="filter.items.value"
              :lifestage-items="filters['lifestageMicroFilters']?.items.value"
              :title="t('filterPopin.customerSegmentation')"
              :default-open="
                Object.keys(checkedItemsState?.consostyleMicroFilters || {}).length > 0 ||
                Object.keys(checkedItemsState?.lifestageMicroFilters || {}).length > 0
              "
              :test-id="'customerSegmentation'"
              :consostyle-loading="filter.isLoading.value"
              :lifestage-loading="filters['lifestageMicroFilters']?.isLoading.value || false"
              :items-count="filter.loadingItemsCount"
              @on-consostyle-checked-items-change="
                (checkedItems: TreeItemsStateType) => handleOnCheckedItemsChange(checkedItems, 'consostyleMicroFilters')
              "
              @on-lifestage-checked-items-change="
                (checkedItems: TreeItemsStateType) => handleOnCheckedItemsChange(checkedItems, 'lifestageMicroFilters')
              "
            />
            <FilterDisclosure
              v-else-if="name !== 'lifestageMicroFilters' && filter.isAvailable"
              :key="filter.label.value"
              :items="filter.items.value"
              :title="filter.label.value"
              :default-open="
                ['assortmentTypologyFilters', 'productTypologyFilters'].includes(name)
                  ? true
                  : Object.keys(checkedItemsState?.[name as FilterNameType] || {}).length > 0
              "
              :test-id="name"
              :is-loading="filter.isLoading.value"
              :items-count="filter.loadingItemsCount"
              :placeholder="filterNeedingSearch.includes(name) ? t('filterPopin.productPlaceholder') : undefined"
              @on-checked-items-change="
                (checkedItems: TreeItemsStateType) => handleOnCheckedItemsChange(checkedItems, name as FilterNameType)
              "
            />
          </div>
        </div>
      </div>
      <div>
        <div class="flex justify-center gap-x-8 mt-4">
          <ButtonBase variant="secondary" size="large" :disabled="isLoading" @click="handleClose">{{
            t('commonLabel.cancel')
          }}</ButtonBase>

          <ButtonBase
            variant="primary"
            size="large"
            :disabled="isLoading"
            :test-id="`apply-filters`"
            @click="handleSelectFilters"
            >{{
              isOnboarding
                ? t('commonLabel.applySelection')
                : t('commonLabel.applicateFilters', {
                    nbFiltersSelected
                  })
            }}</ButtonBase
          >
        </div>
      </div>
    </div>
  </PopUp>
</template>
